import { Migration } from '@gleamer/types';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useMigrationCommand } from '../../../../hooks/useMigration';
import { ButtonTone, PageButton } from '../../../atoms/PageButton';

type MigrationCatchUpPhase = Migration.MigrationCatchUpPhase;
type MigrationCatchUpStatus = Migration.MigrationCatchUpStatus;
type MigrationRunState = Migration.MigrationRunState;
type MigrationSegmentState = Migration.MigrationSegmentState;
type MigrationCommand = Migration.MigrationCommand;
type MigrationRequested = Migration.MigrationRequested;

const RUN_STYLES: Record<MigrationRunState, string> = {
  RUNNING: 'bg-dl-tint text-dl-accent',
  PAUSING: 'bg-dl-warning-tint text-dl-warning',
  PAUSED: 'bg-dl-warning-tint text-dl-warning',
  STOPPING: 'bg-dl-warning-tint text-dl-warning',
  STOPPED: 'bg-dl-track text-dl-text',
  DONE: 'bg-dl-success-tint text-dl-success',
  DONE_WITH_ERRORS: 'bg-dl-warning-tint text-dl-warning',
  FAILED: 'bg-dl-danger-tint text-dl-danger',
};

const RUN_MEANINGS: Record<MigrationRunState, string> = {
  RUNNING: 'Workers claim and copy its segments.',
  PAUSING: 'Its workers finish their batch and claim nothing more.',
  PAUSED: 'Nothing is copied; resuming starts again from the cursors.',
  STOPPING: 'Its workers finish their batch; the run will not start again.',
  STOPPED: 'Stopped by an operator; start a new run to go on.',
  DONE: 'Every segment copied and every error acknowledged.',
  DONE_WITH_ERRORS:
    'Every segment copied; errors wait for an operator to acknowledge them or rerun them. Nothing runs meanwhile.',
  FAILED: 'The run itself failed.',
};

const SEGMENT_STYLES: Record<MigrationSegmentState, string> = {
  PENDING: 'bg-dl-track text-dl-text',
  RUNNING: 'bg-dl-tint text-dl-accent',
  DONE: 'bg-dl-success-tint text-dl-success',
  FAILED: 'bg-dl-danger-tint text-dl-danger',
};

const PHASE_STYLES: Record<MigrationCatchUpPhase, string> = {
  PILING: 'bg-dl-warning-tint text-dl-warning',
  APPLYING: 'bg-dl-tint text-dl-accent',
  LIVE: 'bg-dl-success-tint text-dl-success',
  STOPPING: 'bg-dl-warning-tint text-dl-warning',
  STOPPED: 'bg-dl-track text-dl-text',
};

export const PHASE_LABELS: Record<MigrationCatchUpPhase, string> = {
  PILING: 'Piling',
  APPLYING: 'Applying',
  LIVE: 'Live',
  STOPPING: 'Stopping',
  STOPPED: 'Stopped',
};

/** What a catch-up does in each phase, for the operator wondering why its backlog grows or not. */
export const PHASE_MEANINGS: Record<MigrationCatchUpPhase, string> = {
  PILING:
    'The run copies: g3d’s changes are recorded, and applied once it is done.',
  APPLYING:
    'The run is done: the changes piled while it copied are applied, oldest first.',
  LIVE: 'The backlog is applied: each change is applied as g3d’s events name it.',
  STOPPING:
    'Stopped: the item versions its changes brought are regenerated; nothing more is applied.',
  STOPPED: 'Stopped: changes named after its stop are not applied.',
};

const CHANGE_STYLES: Record<MigrationCatchUpStatus, string> = {
  PENDING: 'bg-dl-track text-dl-text',
  FAILED: 'bg-dl-danger-tint text-dl-danger',
  CONFLICT: 'bg-dl-warning-tint text-dl-warning',
  APPLIED: 'bg-dl-success-tint text-dl-success',
};

function Badge({
  className,
  title,
  children,
}: React.PropsWithChildren<{ className?: string; title?: string }>) {
  return (
    <span
      title={title}
      className={classNames(
        'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold',
        className ?? 'bg-dl-track text-dl-text'
      )}
    >
      {children}
    </span>
  );
}

export function RunStateBadge({ state }: { state: MigrationRunState }) {
  return (
    <Badge className={RUN_STYLES[state]} title={RUN_MEANINGS[state]}>
      {state}
    </Badge>
  );
}

export function SegmentStateBadge({ state }: { state: MigrationSegmentState }) {
  return <Badge className={SEGMENT_STYLES[state]}>{state}</Badge>;
}

export function CatchUpPhaseBadge({ phase }: { phase: MigrationCatchUpPhase }) {
  return (
    <Badge className={PHASE_STYLES[phase]} title={PHASE_MEANINGS[phase]}>
      {PHASE_LABELS[phase]}
    </Badge>
  );
}

export function CatchUpStatusBadge({
  status,
}: {
  status: MigrationCatchUpStatus;
}) {
  return <Badge className={CHANGE_STYLES[status]}>{status}</Badge>;
}

const COUNT_STYLES: Record<Migration.MigrationCountStatus, string> = {
  SAME: 'bg-dl-success-tint text-dl-success',
  EXPLAINED: 'bg-dl-success-tint text-dl-success',
  DIFFERS: 'bg-dl-warning-tint text-dl-warning',
  NOT_MIGRATED: 'bg-dl-track text-dl-text',
  COUNTING: 'bg-dl-tint text-dl-accent',
  COPYING: 'bg-dl-tint text-dl-accent',
};

export function CountStatusBadge({
  status,
  title,
}: {
  status: Migration.MigrationCountStatus;
  title?: string;
}) {
  return (
    <Badge className={COUNT_STYLES[status]} title={title}>
      {Migration.MIGRATION_COUNT_STATUS_LABELS[status]}
    </Badge>
  );
}

export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : value.toLocaleString();
}

// the backoffice's one style for selects (atoms/Surface)
export { selectClassName } from '../../../atoms/Surface';

/** A command's colour says what it does: starting a run is the page's main action (plum), resuming or retrying green, what stops red, what holds or resets amber. */
const COMMAND_TONES: Record<
  MigrationCommand['type'],
  { button: ButtonTone; confirm: ButtonTone }
> = {
  START: { button: 'primary', confirm: 'primary' },
  RESUME: { button: 'go', confirm: 'go' },
  RETRY: { button: 'go-outline', confirm: 'go' },
  RERUN: { button: 'go-outline', confirm: 'go' },
  STOP: { button: 'stop', confirm: 'stop' },
  STOP_CATCH_UP: { button: 'stop', confirm: 'stop' },
  PAUSE: { button: 'caution', confirm: 'caution' },
  REPLAY: { button: 'caution', confirm: 'stop' },
  THROTTLE: { button: 'neutral', confirm: 'primary' },
  ACKNOWLEDGE: { button: 'neutral', confirm: 'primary' },
};

/**
 * A button that sends one command, and says what came of it: accepted (with the request id migration-service
 * logs it under) or refused. A `confirm` text makes the first click ask, inline, before sending.
 */
export function CommandButton({
  command,
  label,
  confirm,
  disabled,
  onSent,
}: {
  command: MigrationCommand;
  label: string;
  confirm?: string;
  disabled?: boolean;
  onSent?: (requested: MigrationRequested) => void;
}) {
  const mutation = useMigrationCommand();
  const [asking, setAsking] = useState(false);
  const tones = COMMAND_TONES[command.type];
  const send = () => {
    setAsking(false);
    mutation.mutate(command, { onSuccess: requested => onSent?.(requested) });
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {asking ? (
        <>
          <span className="text-sm">{confirm}</span>
          <PageButton tone={tones.confirm} onClick={send}>
            Confirm {label.toLowerCase()}
          </PageButton>
          <PageButton onClick={() => setAsking(false)}>Cancel</PageButton>
        </>
      ) : (
        <PageButton
          tone={tones.button}
          onClick={() => (confirm ? setAsking(true) : send())}
          disabled={disabled || mutation.isLoading}
        >
          {mutation.isLoading ? `${label}…` : label}
        </PageButton>
      )}
      {mutation.isSuccess && (
        <span
          role="status"
          className="text-sm text-dl-muted"
          title={`Request ${mutation.data.requestId}`}
        >
          Sent.
        </span>
      )}
      {mutation.isError && (
        <span role="alert" className="text-sm text-dl-danger">
          {(mutation.error as Error).message}
        </span>
      )}
    </span>
  );
}
